import { CommonModule } from '@angular/common';
import type { Signal } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, input, inject } from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { TOfferKind } from '@core/offers-types';
import { OfferKind } from '@core/offers-types';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import type { Placement } from '@core/placements-types';
import { AvatarComponent, HeadingComponent, RemoveHTMLTagsPipe } from '@core/user-interface-legacy';

@Component({
  selector: 'rt-candidate-offer-tooltip',
  imports: [
    CommonModule,
    HeadingComponent,
    OfferStatusIndicatorComponent,
    TranslatePipe,
    AvatarComponent,
    RemoveHTMLTagsPipe,
  ],
  templateUrl: './candidate-offer-tooltip.component.html',
  styleUrl: './candidate-offer-tooltip.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateOfferTooltipComponent {
  private readonly i18n = inject(I18nService);

  public readonly OfferKind: typeof OfferKind = OfferKind;
  public placement = input.required<Placement>();

  public offerData = computed(() =>
    this.placement().talentPoolId ? this.placement().talentPool! : this.placement().offer!,
  );

  public job = computed(() => this.placement().offer!);
  public talentPool = computed(() => this.placement().talentPool!);

  public kind: Signal<TOfferKind> = computed(() => this.offerData().kind);

  public offersStatuses: { [key: string]: string } = {
    published: this.i18n.translate(`app.offers.status.published`),
    internal: this.i18n.translate(`app.offers.status.internal`),
    draft: this.i18n.translate(`app.offers.status.draft`),
    closed: this.i18n.translate(`app.offers.status.closed`),
    archived: this.i18n.translate(`app.offers.status.archived`),
  };

  public workModels: { [key: string]: string } = {
    onSite: this.i18n.translate('app.offer_form.work_model_section.on_site.title'),
    hybrid: this.i18n.translate('app.offer_form.work_model_section.hybrid.title'),
    remote: this.i18n.translate('app.offer_form.work_model_section.remote.title'),
  };

  public workModelCopy = computed(() => {
    const models = [
      ...(this.job().onSite ? [this.workModels['onSite']] : []),
      ...(this.job().remote ? [this.workModels['remote']] : []),
      ...(this.job().hybrid ? [this.workModels['hybrid']] : []),
    ];
    return models.join(', ');
  });
}
